import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Building2, Users, Calendar, Clock, MapPin } from 'lucide-react';
import { format } from 'date-fns';

const STATUS_OPTIONS = [
  { value: 'requested', label: 'Requested' },
  { value: 'planning', label: 'Planning' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
];

export const DetailsTab = ({ project, partner, isEditing, editData, setEditData }) => (
  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <Card className="lg:col-span-2">
      <CardHeader>
        <CardTitle>Project Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isEditing ? (
          <>
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={editData.title}
                onChange={(e) => setEditData({ ...editData, title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={editData.description}
                onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Project Manager</Label>
              <Input
                value={editData.pm_name}
                onChange={(e) => setEditData({ ...editData, pm_name: e.target.value })}
                placeholder="Project manager name"
                data-testid="edit-pm-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={editData.status} onValueChange={(v) => setEditData({ ...editData, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={editData.notes}
                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                rows={3}
              />
            </div>
          </>
        ) : (
          <>
            {project.pm_name && (
              <div>
                <p className="text-sm text-muted-foreground">Project Manager</p>
                <p className="mt-1 font-medium">{project.pm_name}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-muted-foreground">Description</p>
              <p className="mt-1 whitespace-pre-wrap">{project.description || 'No description provided'}</p>
            </div>
            {project.notes && (
              <div>
                <p className="text-sm text-muted-foreground">Notes</p>
                <p className="mt-1 whitespace-pre-wrap">{project.notes}</p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>

    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-base">Partner</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-secondary" />
            </div>
            <div>
              <p className="font-medium">{partner?.name || 'Unknown'}</p>
              <p className="text-sm text-muted-foreground">{partner?.code}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Requestor</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-muted-foreground" />
            <span>{project.requestor_name || 'N/A'}</span>
          </div>
          <p className="text-sm text-muted-foreground">{project.requestor_email || 'N/A'}</p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Requested Date</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <span>{project.request_date ? format(new Date(project.request_date), 'MMM d, yyyy') : 'N/A'}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <Badge variant="outline">{project.delivery_mode}</Badge>
          </div>
          {project.location && (
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm">{project.location}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  </div>
);

export default DetailsTab;
